/**
 * 脑波数据处理 Composable
 * 管理虚拟脑波数据、波形数据、睡眠状态映射、显示更新等
 */

import * as sleepDataUtils from '@/utils/sleepDataUtils';
import logger from '@/utils/logger';
import config from '@/config';
import {
  createWavePoint,
  trimWaveBandInPlace,
  ensureWaveDataShell,
  WAVE_BANDS
} from '@/utils/waveDataUtils';

export default function useBrainwaveData() {
  return {
    methods: {
      /**
       * 更新虚拟脑波数据
       * 为虚拟预约数据生成和更新脑波数据，确保前端展示连贯性
       */
      updateVirtualBrainwaveData() {
        if (!this.appointmentData || this.appointmentData.length === 0) {
          return;
        }

        // 仅处理虚拟数据；真实数据不展示虚拟脑波，只有后端提供脑波时才展示
        const virtualAppointments = this.appointmentData.filter(appointment =>
          appointment && (appointment.isMock || !appointment.roomId)
        );

        if (virtualAppointments.length === 0) {
          return;
        }

        virtualAppointments.forEach(appointment => {
          // 生成新的虚拟脑波数据
          const virtualBrainwaveData = this.generateVirtualBrainwaveData(
            appointment.patientName,
            appointment.realtime_sleep_state,
            appointment
          );

          // 防御性检查：确保虚拟数据不为0
          const hasValidVirtualData = virtualBrainwaveData.delta > 0 || virtualBrainwaveData.theta > 0 ||
            virtualBrainwaveData.alpha > 0 || virtualBrainwaveData.beta > 0 ||
            virtualBrainwaveData.gamma > 0;

          if (hasValidVirtualData) {
            appointment.delta = virtualBrainwaveData.delta;
            appointment.theta = virtualBrainwaveData.theta;
            appointment.alpha = virtualBrainwaveData.alpha;
            appointment.beta = virtualBrainwaveData.beta;
            appointment.gamma = virtualBrainwaveData.gamma;
          } else {
            logger.warn(`虚拟预约 ${appointment.patientName} 生成的脑波数据全部为0，保持当前值`, {
              生成的虚拟数据: virtualBrainwaveData,
              当前值: {
                delta: appointment.delta,
                theta: appointment.theta,
                alpha: appointment.alpha,
                beta: appointment.beta,
                gamma: appointment.gamma
              }
            });
          }

          // 确保显示脑波图波形
          if (typeof appointment.hideWaveform === 'undefined') {
            this.$set(appointment, 'hideWaveform', false);
          } else {
            appointment.hideWaveform = false;
          }

          // 更新脑波图数据
          this.updateWaveData(appointment, {
            delta: virtualBrainwaveData.delta / 100,
            theta: virtualBrainwaveData.theta / 100,
            alpha: virtualBrainwaveData.alpha / 100,
            beta: virtualBrainwaveData.beta / 100,
            gamma: virtualBrainwaveData.gamma / 100
          });

          // 缓存虚拟脑波数据
          appointment.lastValidBrainwaveData = {
            delta: appointment.delta,
            theta: appointment.theta,
            alpha: appointment.alpha,
            beta: appointment.beta,
            gamma: appointment.gamma,
            timestamp: Date.now()
          };
        });
      },

      /**
       * 处理脑波数据异常情况
       * @param {Object} appointment - 预约数据
       */
      handleBrainwaveDataError(appointment) {
        appointment.delta = null;
        appointment.theta = null;
        appointment.alpha = null;
        appointment.beta = null;
        appointment.gamma = null;

        appointment.realtime_sleep_state = '清醒';

        if (typeof appointment.hideWaveform === 'undefined') {
          this.$set(appointment, 'hideWaveform', false);
        } else {
          appointment.hideWaveform = false;
        }

        appointment.quality_score = 0;
      },

      /**
       * 清除脑波图中的所有波形数据
       * @param {Object} appointment - 预约数据
       */
      clearWaveformData(appointment) {
        ensureWaveDataShell(appointment);
        WAVE_BANDS.forEach((type) => {
          appointment.waveData[type].length = 0;
        });
      },

      /**
       * 映射睡眠状态
       * @param {string} apiSleepState - API 返回的睡眠状态
       * @returns {string} 中文睡眠状态
       */
      mapSleepState(apiSleepState) {
        const stateMap = {
          'wake': '清醒',
          'n1': '浅睡',
          'n2': '浅睡',
          'n3': '深睡',
          'rem': '浅睡',
          'awake': '清醒',
          'drowsy': '浅睡',
          'light_sleep': '浅睡',
          'deep_sleep': '深睡',
          'rem_sleep': '浅睡'
        };

        return stateMap[apiSleepState] || '清醒';
      },

      /**
       * 更新脑波图数据
       * @param {Object} appointment - 预约数据
       * @param {Object} frequencyBands - 频率带数据
       */
      updateWaveData(appointment, frequencyBands) {
        if (this.getTreatmentStatus(appointment) === '已结束') {
          return;
        }

        if (!frequencyBands || typeof frequencyBands !== 'object') {
          return;
        }

        ensureWaveDataShell(appointment);

        const maxLen = Number(config.performance && config.performance.waveHistoryLimit) || 300;
        const isRealPatient = !appointment.isMock && appointment.roomId;

        Object.keys(frequencyBands).forEach(band => {
          if (appointment.waveData[band]) {
            const bandValue = frequencyBands[band];
            const isValidValue = typeof bandValue === 'number' &&
              !isNaN(bandValue) &&
              isFinite(bandValue) &&
              bandValue >= 0;

            if (isValidValue) {
              const percentage = bandValue * 100;
              let baseValue = Math.round(percentage);

              const treatmentStatus = this.getTreatmentStatus(appointment);
              const isTreatmentEnded = treatmentStatus === '已结束';

              // 真实患者：未收到非零脑波时不写入波线点
              if (isRealPatient && bandValue === 0) {
                return;
              }

              if (bandValue === 0 && !isTreatmentEnded) {
                baseValue = 1;
              }

              if (bandValue > 0 && baseValue === 0 && !isTreatmentEnded) {
                baseValue = 1;
              }

              if (percentage > 0 && percentage < 0.5 && !isTreatmentEnded) {
                baseValue = 1;
              }

              if (baseValue >= 0) {
                if (baseValue === 0 && !isTreatmentEnded && appointment.waveData[band].length > 0) {
                  const lastValue = appointment.waveData[band][appointment.waveData[band].length - 1].value;
                  if (lastValue && lastValue > 0) {
                    baseValue = lastValue;
                  }
                }

                appointment.waveData[band].push(createWavePoint(baseValue));
                trimWaveBandInPlace(appointment.waveData[band], maxLen);
              }
            } else {
              if (appointment.waveData[band].length > 0) {
                const lastValidPoint = appointment.waveData[band][appointment.waveData[band].length - 1];
                if (lastValidPoint && typeof lastValidPoint.value === 'number' && lastValidPoint.value >= 0) {
                  appointment.waveData[band].push(
                    createWavePoint(lastValidPoint.value, Date.now(), { interpolated: true })
                  );
                  trimWaveBandInPlace(appointment.waveData[band], maxLen);
                }
              }
            }
          }
        });
      },

      /**
       * 每秒切换显示 realtimeDataArray 中的脑波数据点
       */
      updateBrainwaveDisplay() {
        if (!this.appointmentData || this.appointmentData.length === 0) {
          return;
        }

        this.appointmentData.forEach(appointment => {
          if (!appointment || !appointment.roomId) {
            return;
          }

          if (this.getTreatmentStatus(appointment) === '已结束') {
            return;
          }

          const beforeUpdate = {
            roomId: appointment.roomId,
            delta: appointment.delta,
            theta: appointment.theta,
            alpha: appointment.alpha,
            beta: appointment.beta,
            gamma: appointment.gamma,
            hasRealtimeArray: !!(appointment.realtimeDataArray && appointment.realtimeDataArray.length > 0),
            arrayLength: appointment.realtimeDataArray?.length || 0,
            currentDisplayIndex: appointment.currentDisplayIndex
          };

          if (!appointment.realtimeDataArray || !Array.isArray(appointment.realtimeDataArray) || appointment.realtimeDataArray.length === 0) {
            if (appointment.lastValidBrainwaveData) {
              const cached = appointment.lastValidBrainwaveData;
              const brainwaveBands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
              brainwaveBands.forEach(band => {
                const currentValue = appointment[band];
                const cachedValue = cached[band];
                if ((currentValue === undefined || currentValue === null || currentValue === 0) &&
                  cachedValue !== undefined && cachedValue !== null && cachedValue !== 0) {
                  appointment[band] = cachedValue;
                }
              });

            }
            return;
          }

          const dataArray = appointment.realtimeDataArray;

          if (appointment.currentDisplayIndex === undefined) {
            appointment.currentDisplayIndex = dataArray.length - 1;
          }

          const oldIndex = appointment.currentDisplayIndex;
          appointment.currentDisplayIndex = (appointment.currentDisplayIndex + 1) % dataArray.length;

          const displayData = dataArray[appointment.currentDisplayIndex];


          if (displayData && displayData.frequency_bands) {
            const displayBands = displayData.frequency_bands;
            const treatmentStatus = this.getTreatmentStatus(appointment);
            const isTreatmentEnded = treatmentStatus === '已结束';

            const isValidBands = displayBands &&
              typeof displayBands.delta === 'number' &&
              typeof displayBands.theta === 'number' &&
              typeof displayBands.alpha === 'number' &&
              typeof displayBands.beta === 'number' &&
              typeof displayBands.gamma === 'number' &&
              !isNaN(displayBands.delta) && !isNaN(displayBands.theta) && !isNaN(displayBands.alpha) && !isNaN(displayBands.beta) && !isNaN(displayBands.gamma) &&
              isFinite(displayBands.delta) && isFinite(displayBands.theta) && isFinite(displayBands.alpha) && isFinite(displayBands.beta) && isFinite(displayBands.gamma);

            if (isValidBands) {
              const convertToPercentage = (value) => {
                const percentage = value * 100;
                const rounded = Math.round(percentage);
                if (value === 0 && !isTreatmentEnded) return 1;
                if (rounded === 0 && percentage > 0) return 1;
                if (percentage > 0 && percentage < 0.5) return 1;
                return rounded;
              };

              const afterUpdate = {
                delta: convertToPercentage(displayBands.delta),
                theta: convertToPercentage(displayBands.theta),
                alpha: convertToPercentage(displayBands.alpha),
                beta: convertToPercentage(displayBands.beta),
                gamma: convertToPercentage(displayBands.gamma)
              };

              const becameZero = [];
              const bands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
              bands.forEach(band => {
                if (beforeUpdate[band] !== undefined && beforeUpdate[band] !== 0 &&
                  (afterUpdate[band] === undefined || afterUpdate[band] === 0)) {
                  becameZero.push({
                    band,
                    before: beforeUpdate[band],
                    after: afterUpdate[band],
                    rawValue: displayBands[band]
                  });
                }
              });

              if (becameZero.length > 0) {
              } else {
              }

              appointment.delta = afterUpdate.delta;
              appointment.theta = afterUpdate.theta;
              appointment.alpha = afterUpdate.alpha;
              appointment.beta = afterUpdate.beta;
              appointment.gamma = afterUpdate.gamma;

              appointment.lastValidBrainwaveData = {
                delta: appointment.delta,
                theta: appointment.theta,
                alpha: appointment.alpha,
                beta: appointment.beta,
                gamma: appointment.gamma,
                timestamp: Date.now()
              };
            }
          }
        });
      },

      /**
       * 为虚拟数据生成脑波数据（百分比，总和为100%）
       * @param {string} patientName - 患者姓名
       * @param {string} currentSleepState - 当前睡眠状态，默认为'浅睡'
       * @returns {Object} 脑波数据对象
       */
      generateVirtualBrainwaveData(patientName, currentSleepState = '浅睡', context = {}) {
        return sleepDataUtils.generateVirtualBrainwaveData(patientName, currentSleepState, context);
      }
    }
  };
}
